// Descarga de un asset generado (imagen o video), con el MISMO mecanismo que usa el
// resto de la app en `GeneratedImagesInline`.
//
// Por qué no alcanza con `<a href download>` —que es lo que tenía el onboarding—:
// **Safari en iOS ignora el atributo `download`**. En vez de guardar el archivo, abre
// la imagen en una vista nueva y el usuario se queda sin nada (y encima con la sesión
// del onboarding atrás). Por eso la app usa `navigator.share` en iOS: es el camino que
// ofrece "Guardar en Fotos" / "Guardar video". El `<a>` queda sólo como fallback, y
// sobre un blob local (no sobre la URL remota), porque `download` cross-origin también
// se ignora en varios navegadores.
//
// Ver ADR-2026-08-03-descarga-del-onboarding-usa-el-mecanismo-de-la-app.

export const esIOS = (): boolean =>
  typeof navigator !== 'undefined' && /iPhone|iPad|iPod/i.test(navigator.userAgent);

const puedeCompartirArchivos = (): boolean =>
  typeof navigator !== 'undefined' &&
  typeof navigator.share === 'function' &&
  typeof navigator.canShare === 'function';

/** Resultado de un intento de compartir: el `aborted` (el usuario cerró la hoja) NO
 *  debe caer al fallback — sería descargar algo que el usuario acaba de cancelar. */
export type ResultadoCompartir = 'shared' | 'aborted' | 'failed';

/** Mime de imagen a partir de la extensión del nombre; cae al del blob, o a PNG. */
function mimeDeImagen(fileName: string, blob: Blob): string {
  const ext = (fileName.match(/\.([a-z0-9]+)$/i)?.[1] || '').toLowerCase();
  if (ext === 'jpg' || ext === 'jpeg') return 'image/jpeg';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'png') return 'image/png';
  return blob.type.startsWith('image/') ? blob.type : 'image/png';
}

/** Comparte una IMAGEN por la hoja del sistema (iOS → "Guardar en Fotos"). */
export async function compartirImagenIOS(
  raw: Blob,
  fileName: string,
): Promise<ResultadoCompartir> {
  const mime = mimeDeImagen(fileName, raw);
  const file = new File([raw.type === mime ? raw : new Blob([raw], { type: mime })], fileName, {
    type: mime,
  });
  if (navigator.canShare?.({ files: [file] })) {
    try {
      await navigator.share({ files: [file] });
      return 'shared';
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return 'aborted';
    }
  }
  return 'failed';
}

/**
 * Comparte un VIDEO. Prueba `video/mp4` y, si el sistema no lo acepta, reintenta como
 * `video/quicktime` (.mov) — hay versiones de iOS que sólo aceptan el segundo.
 */
export async function compartirVideoIOS(
  raw: Blob,
  fileName: string,
): Promise<ResultadoCompartir> {
  const intentos: Array<{ mime: string; nombre: string }> = [
    { mime: 'video/mp4', nombre: fileName },
    { mime: 'video/quicktime', nombre: fileName.replace(/\.mp4$/, '.mov') },
  ];
  for (const { mime, nombre } of intentos) {
    const file = new File([new Blob([raw], { type: mime })], nombre, { type: mime });
    if (!navigator.canShare?.({ files: [file] })) continue;
    try {
      await navigator.share({ files: [file] });
      return 'shared';
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') return 'aborted';
    }
  }
  return 'failed';
}

/** Descarga clásica: blob local + `<a download>` + limpieza del object URL. */
function descargarBlob(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.style.display = 'none';
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  // La limpieza va diferida: revocar el object URL en el mismo tick cancela la
  // descarga en algunos navegadores (el click todavía no la levantó).
  setTimeout(() => {
    window.URL.revokeObjectURL(url);
    a.remove();
  }, 100);
}

export interface DescargarDesdeBlobOpts {
  /** El archivo YA materializado en memoria. */
  blob: Blob;
  /** Nombre final del archivo, con extensión. Usar `buildDownloadFileName`. */
  fileName: string;
  /** Trata el asset como video (cambia la rama de compartir en iOS). */
  esVideo?: boolean;
}

/**
 * Entrega un archivo que ya está en memoria: iOS → hoja de compartir; el resto →
 * descarga clásica. Es el núcleo de `descargarArchivo`, expuesto aparte para los casos
 * donde el blob NO viene de una URL sino que se genera en el cliente — hoy, el export
 * del editor de Edición Canva, que rasteriza la escena a PNG con canvas y ya tiene el
 * blob en la mano (ver ADR-2026-08-04-descarga-del-editor-usa-el-mecanismo-de-la-app).
 *
 * Devuelve `true` si el archivo llegó a destino; `false` si el usuario canceló la hoja.
 */
export async function descargarDesdeBlob({
  blob,
  fileName,
  esVideo = false,
}: DescargarDesdeBlobOpts): Promise<boolean> {
  if (esIOS() && puedeCompartirArchivos()) {
    const resultado = esVideo
      ? await compartirVideoIOS(blob, fileName)
      : await compartirImagenIOS(blob, fileName);
    if (resultado === 'shared') return true;
    if (resultado === 'aborted') return false;
    // 'failed' → el sistema no aceptó el archivo (o Safari perdió la activación por
    // gesto de usuario si pasó demasiado tiempo desde el click); cae a la descarga
    // clásica, que es exactamente el comportamiento que había antes.
  }

  descargarBlob(blob, fileName);
  return true;
}

export interface DescargarArchivoOpts {
  /** URL del asset (firmada o pública). */
  url: string;
  /** Nombre final del archivo, con extensión. Usar `buildDownloadFileName`. */
  fileName: string;
  /** Trata el asset como video (cambia la rama de compartir en iOS). */
  esVideo?: boolean;
}

/**
 * Descarga un asset generado. En iOS abre la hoja de compartir (única forma de que
 * quede guardado de verdad); en el resto, descarga directa. Si el usuario CANCELA la
 * hoja, no se descarga nada — cancelar significa cancelar.
 *
 * Devuelve `true` si el archivo llegó a destino (compartido o descargado).
 * Lanza si no se pudo ni siquiera traer el archivo: el caller decide qué mostrar.
 */
export async function descargarArchivo({
  url,
  fileName,
  esVideo = false,
}: DescargarArchivoOpts): Promise<boolean> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`No se pudo descargar el archivo (HTTP ${response.status})`);
  return descargarDesdeBlob({ blob: await response.blob(), fileName, esVideo });
}

export default descargarArchivo;
